import type { QueryClient } from "@tanstack/react-query";
import type { useTranslations } from "next-intl";
import type { ServerError } from "@/lib/api/errors/ErrorHandler";
import { roleTemplatesKey } from "@/lib/authz/roleTemplateQueryKeys";

type Translator = ReturnType<typeof useTranslations>;

export function isServerError(v: unknown): v is ServerError {
  return !!v && typeof v === "object" && "isServerError" in v && (v as { isServerError?: unknown }).isServerError === true;
}

/** The event's role templates as the Roles page reads them (full projection) — see `roleTemplateQueryKeys`. */
export { roleTemplatesKey };

/**
 * After a template write: the Roles list (and the editor's template, read off
 * it) and the custom roles the member pages offer (`…collab, templates`).
 */
export async function invalidateRoleTemplates(queryClient: QueryClient, eventId: string) {
  await Promise.all([
    queryClient.invalidateQueries({ queryKey: roleTemplatesKey(eventId) }),
    queryClient.invalidateQueries({ queryKey: ["eventSettings", eventId, "collab", "templates"] }),
  ]);
}

/**
 * B2 (plan Rev. 5.2, FE-5): the role-template codes with copy of their own
 * under `roleTemplates.errors.*` — never the generic `errors.versionMismatch`
 * ("Settings changed in another tab"), which says nothing about a field change
 * elsewhere and offers no way out.
 */
export type RoleTemplateConflictCode = "versionMismatch" | "ifMatchRequired";

/** The backend code a refusal carries — `detail` when it holds a payload, else `message`. */
function codeOf(res: { message: string; detail?: string }): { head: string; tail: string } {
  const code = res.detail && res.detail.includes(":") ? res.detail : res.message;
  const colon = code.indexOf(":");
  const head = colon === -1 ? code : code.slice(0, colon);
  const rawTail = colon === -1 ? "" : code.slice(colon + 1);
  return { head: head.trim(), tail: rawTail.split(" — ")[0].trim() };
}

/** The bare head of a code string (`invalidExclusionKey:x` → `invalidExclusionKey`). */
const headOf = (code: string): string => {
  const colon = code.indexOf(":");
  return (colon === -1 ? code : code.slice(0, colon)).trim();
};

/**
 * A save refused because the editor's seed is stale: 409 `versionMismatch`
 * (someone — another tab, a field cascade — wrote since the seed) or 400
 * `ifMatchRequired` (an "All except" template written without If-Match).
 * Takes a `ServerError` (handlers) or a thrown Error whose message is the code
 * (clients). Anything else ⇒ null.
 */
export function roleTemplateConflictOf(res: unknown): RoleTemplateConflictCode | null {
  if (!res || typeof res !== "object") return null;
  const r = res as { message?: unknown; detail?: unknown };
  const heads = [r.message, r.detail].filter((v): v is string => typeof v === "string").map(headOf);
  if (heads.includes("versionMismatch")) return "versionMismatch";
  if (heads.includes("ifMatchRequired")) return "ifMatchRequired";
  return null;
}

/**
 * The role-specific text of a B2 code, or null when `code` is none of them.
 * `tRoles` is the `roleTemplates` translator; the keys are literal so the
 * i18n gates see them.
 */
export function roleTemplateB2CodeText(tRoles: Translator, code: string | null | undefined): string | null {
  if (!code) return null;
  const head = headOf(code);
  const colon = code.indexOf(":");
  const tail = colon === -1 ? "" : code.slice(colon + 1).split(" — ")[0].trim();
  switch (head) {
    case "versionMismatch":
      return tRoles("errors.versionMismatch");
    case "ifMatchRequired":
      return tRoles("errors.ifMatchRequired");
    case "invalidFieldAllowlistMode":
      return tRoles("errors.invalidFieldAllowlistMode");
    case "invalidExclusionKey": {
      const text = tRoles("errors.invalidExclusionKey");
      return tail ? `${text} (${tail})` : text;
    }
    default:
      return null;
  }
}

/**
 * A template call's refusal as text (moved out of RoleTemplatesCard).
 *
 * `ServerError.message` carries only the resolved errorCode; the backend's raw
 * text — which is where the OFFENDING KEY lives (`invalidRowFilterKey:tierr`,
 * `invalidFilterValue:additionalFields.tier`) — lands in `detail`. Reading
 * `message` alone meant the toast never named the row, on a form that allows
 * up to 50 of them. Prefer `detail` when it carries a payload.
 *
 * The backend sometimes appends a payload after a colon
 * (`fieldNotAllowed:firstName,lastName`, `unknownCapability:guests:invent`):
 * the bare key is translated and the payload re-appended for context.
 * `unknownFilterKey` appends the entire valid-key catalogue after an em dash —
 * useful for an AI caller, unreadable in a toast — so only the key stays.
 *
 * B2: the four role-template codes (`versionMismatch`, `ifMatchRequired`,
 * `invalidFieldAllowlistMode`, `invalidExclusionKey:<k>`) read their
 * role-specific copy through `tRoles` (the `roleTemplates` translator).
 */
export function roleTemplateErrorText(
  tErrors: Translator,
  res: { message: string; detail?: string },
  tRoles: Translator,
): string {
  const { head, tail } = codeOf(res);
  const b2 = roleTemplateB2CodeText(tRoles, tail ? `${head}:${tail}` : head) ?? roleTemplateB2CodeText(tRoles, res.message);
  if (b2) return b2;
  // A key without copy would render its path: `.has` first (errors guard G3a).
  const resolved = tErrors.has(head) ? tErrors(head) : tErrors("unknown");
  return tail ? `${resolved} (${tail})` : resolved;
}
